import { useCallback, useState } from 'react';
import { Canvas, IText } from 'fabric';
import { usePieceDispatch } from '../../model/piece-context';
import { useSelectedLayer, useSelectedLayerId } from '../../selection/hooks/use-selected-layer';
import type { TextContent } from '../../model/types';
import { readSelectionStyle, selectionRange, writeSelectionStyle } from '../utils/selection-style';
import { useOnSelectionChange } from './use-on-selection-change';

type StyleFields = Required<Pick<TextContent, 'fontFamily' | 'fontSize' | 'fontWeight' | 'fontStyle' | 'underline'>>;

const useTextContent = (): TextContent | null => {
  const layer = useSelectedLayer();
  return layer?.kind === 'text' ? layer.content : null;
};

const readField = <F extends keyof StyleFields>(textObject: IText, field: F): StyleFields[F] | null => {
  const style = readSelectionStyle(textObject) as Partial<StyleFields> | null;
  return style?.[field] ?? null;
};

export function useTextStyleField<F extends keyof StyleFields>(
  textObject: IText,
  canvas: Canvas,
  field: F,
  defaultValue: StyleFields[F]
): [StyleFields[F], (value: StyleFields[F]) => void] {
  const dispatch = usePieceDispatch();
  const layerId = useSelectedLayerId();
  const content: Partial<StyleFields> | null = useTextContent();

  const [selected, setSelected] = useState<StyleFields[F] | null>(null);

  const read = useCallback(() => setSelected(readField(textObject, field)), [textObject, field]);
  useOnSelectionChange(textObject, read);

  const apply = useCallback(
    (next: StyleFields[F]) => {
      if (!layerId) return;

      if (!selectionRange(textObject)) {
        dispatch({ type: 'patchLayerContent', id: layerId, patch: { [field]: next } });
        return;
      }

      setSelected(next);
      const styles = writeSelectionStyle(textObject, field, next);
      canvas.requestRenderAll();
      dispatch({ type: 'patchLayerContent', id: layerId, patch: { styles } });
    },
    [dispatch, layerId, textObject, canvas, field]
  );

  return [selected ?? content?.[field] ?? defaultValue, apply];
}

/** Object-level only: Fabric has no per-character lineHeight. */
export function useTextLineHeight(defaultValue: number): [number, (value: number) => void] {
  const dispatch = usePieceDispatch();
  const layerId = useSelectedLayerId();
  const content = useTextContent();

  const apply = useCallback(
    (lineHeight: number) => {
      if (layerId) dispatch({ type: 'patchLayerContent', id: layerId, patch: { lineHeight } });
    },
    [dispatch, layerId]
  );

  return [content?.lineHeight ?? defaultValue, apply];
}
